"use client"

import { useState } from "react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../../components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Trash2, Plus, MoveUp, MoveDown, Save, Share2, Link } from "lucide-react"
import { Label } from "../../components/ui/label"
import { Switch } from "../../components/ui/switch"
import { Textarea } from "../../components/ui/textarea"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog"
import { useToast } from "../../components/ui/use-toast"

export default function FormBuilder({
  form,
  onFormChange,
  onSave,
  deleteForm,
  finalizeForm,
  shareForm,
  updatingForm,
  deletingForm,
}) {
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const { toast } = useToast();

  const handleTitleChange = (e) => {
    onFormChange(form.id, {
      title: e.target.value,
    });
  };

  const handleDescriptionChange = (e) => {
    onFormChange(form.id, {
      description: e.target.value,
    });
  };

  const addField = () => {
    const newField = {
      id: Date.now().toString(),
      type: "text",
      label: "Nuevo Campo",
      placeholder: "",
      required: false,
      options: [],
      conditionalLogic: {
        enabled: false,
        dependsOn: "",
        showWhen: "",
        value: "",
      },
    };

    onFormChange(form.id, {
      fields: [...form.fields, newField],
    });
  };

  const updateField = (fieldId, updates) => {
    const updatedFields = form.fields.map((field) =>
      field.id === fieldId ? { ...field, ...updates } : field
    );
    onFormChange(form.id, {
      fields: updatedFields,
    });
  };

  const removeField = (fieldId) => {
    const updatedFields = form.fields.filter((field) => field.id !== fieldId);
    onFormChange(form.id, {
      fields: updatedFields,
    });
  };

  const moveField = (fieldId, direction) => {
    const fieldIndex = form.fields.findIndex((field) => field.id === fieldId);
    if (
      (direction === "up" && fieldIndex === 0) ||
      (direction === "down" && fieldIndex === form.fields.length - 1)
    ) {
      return;
    }

    const newFields = [...form.fields];
    const newIndex = direction === "up" ? fieldIndex - 1 : fieldIndex + 1;

    // Swap the fields
    [newFields[fieldIndex], newFields[newIndex]] = [
      newFields[newIndex],
      newFields[fieldIndex],
    ];

    onFormChange(form.id, {
      fields: newFields,
    });
  };

  const addOption = (fieldId) => {
    const field = form.fields.find((f) => f.id === fieldId);
    if (!field) return;

    const newOption = {
      id: Date.now().toString(),
      label: `Opción ${field.options.length + 1}`,
      value: `option-${field.options.length + 1}`,
    };

    const updatedOptions = [...field.options, newOption];
    updateField(fieldId, { options: updatedOptions });
  };

  const updateOption = (fieldId, optionId, label) => {
    const field = form.fields.find((f) => f.id === fieldId);
    if (!field) return;

    const updatedOptions = field.options.map((option) =>
      option.id === optionId
        ? { ...option, label, value: label.toLowerCase().replace(/\s+/g, "-") }
        : option
    );

    updateField(fieldId, { options: updatedOptions });
  };

  const removeOption = (fieldId, optionId) => {
    const field = form.fields.find((f) => f.id === fieldId);
    if (!field) return;

    const updatedOptions = field.options.filter(
      (option) => option.id !== optionId
    );
    updateField(fieldId, { options: updatedOptions });
  };

  const saveForm = () => {
    onSave(form);
    toast({
      title: "Formulario guardado",
      description: "Los cambios han sido guardados correctamente.",
    });
  };

  const handleDeleteForm = () => {
    deleteForm(form._id);
  };

  const handleFinalizeForm = () => {
    const updatedForm = {
      ...form,
      finalized: true,
      finalizedAt: new Date().toISOString(),
    };

    onFormChange(form.id, {
      finalized: true,
      finalizedAt: new Date().toISOString(),
    });
    finalizeForm(updatedForm);
    setShowFinalizeDialog(false);

    toast({
      title: "Formulario finalizado",
      description:
        "El formulario ha sido finalizado y ahora puede ser compartido.",
    });

    // Generate share link
    const shareUrl = generateShareLink(updatedForm.id);
    setShareLink(shareUrl);
    setShowShareDialog(true);
  };

  const handleShareForm = () => {
    if (!form.finalized) {
      setShowFinalizeDialog(true);
      return;
    }

    const shareUrl = generateShareLink(form.id);
    setShareLink(shareUrl);
    setShowShareDialog(true);
  };

  const generateShareLink = (formId) => {
    // In a real application, this would generate a proper URL
    // For this example, we'll just create a mock URL
    return `${window.location.origin}/forms/shared/${formId}`;
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast({
      title: "Enlace copiado",
      description: "El enlace ha sido copiado al portapapeles.",
    });
  };

  return (
    <div className="space-y-6">
      <Card
        className="shadow-lg border-t-4 transition-all duration-200 hover:shadow-xl"
        style={{
          borderTopColor: "#f97316",
          borderRadius: "16px",
        }}
      >
        <CardHeader
          className="bg-gray-50 p-6"
          style={{ borderRadius: "16px 16px 0 0" }}
        >
          <CardTitle>
            <Input
              value={form.title}
              onChange={handleTitleChange}
              className="text-2xl font-bold bg-transparent border-none shadow-none focus-visible:ring-0 px-0 placeholder:text-gray-400"
              style={{ color: "#f97316", borderRadius: "8px" }}
              placeholder="Título del formulario"
            />
          </CardTitle>
          <Textarea
            value={form.description || ""}
            onChange={handleDescriptionChange}
            placeholder="Descripción del formulario (opcional)"
            className="mt-2 resize-none bg-transparent border-none shadow-none focus-visible:ring-0 px-0 placeholder:text-gray-400"
            style={{ color: "#f97316", borderRadius: "8px" }}
          />
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {form.fields.map((field, index) => (
              <Card
                key={field.id}
                className="border-l-4 shadow-sm hover:shadow-md transition-all duration-200 group"
                style={{
                  borderLeftColor: "#f97316",
                  borderRadius: "12px",
                }}
              >
                <CardHeader
                  className="pb-2 flex flex-row items-center justify-between space-y-0 bg-gray-50 p-4"
                  style={{ borderRadius: "12px 12px 0 0" }}
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <Input
                      value={field.label}
                      onChange={(e) =>
                        updateField(field.id, { label: e.target.value })
                      }
                      className="font-medium bg-white border border-gray-200 shadow-sm focus-visible:ring-0 px-3 placeholder:text-gray-400 flex-1"
                      style={{
                        color: "#374151",
                        borderRadius: "8px",
                      }}
                      placeholder="Etiqueta del campo"
                    />
                    <Select
                      value={field.type}
                      onValueChange={(value) =>
                        updateField(field.id, { type: value })
                      }
                    >
                      <SelectTrigger
                        className="w-[180px] bg-white border border-gray-200"
                        style={{
                          borderRadius: "8px",
                        }}
                      >
                        <SelectValue placeholder="Tipo de campo" />
                      </SelectTrigger>
                      <SelectContent
                        className="bg-white border border-gray-200"
                      >
                        <SelectItem value="text">Texto</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="number">Número</SelectItem>
                        <SelectItem value="tel">Teléfono</SelectItem>
                        <SelectItem value="textarea">Área de texto</SelectItem>
                        <SelectItem value="select">Selección</SelectItem>
                        <SelectItem value="radio">Opciones (Radio)</SelectItem>
                        <SelectItem value="checkbox">Casilla de verificación</SelectItem>
                        <SelectItem value="file">Documento</SelectItem>
                        <SelectItem value="date">Fecha</SelectItem>
                        <SelectItem value="time">Hora</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => moveField(field.id, "up")}
                      disabled={index === 0}
                      className="h-8 w-8 hover:bg-gray-100 transition-all duration-200 opacity-0 group-hover:opacity-100 bg-white border border-gray-200"
                      style={{ borderRadius: "8px" }}
                    >
                      <MoveUp className="h-4 w-4 text-gray-900" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => moveField(field.id, "down")}
                      disabled={index === form.fields.length - 1}
                      className="h-8 w-8 hover:bg-gray-100 transition-all duration-200 opacity-0 group-hover:opacity-100 bg-white border border-gray-200"
                      style={{ borderRadius: "8px" }}
                    >
                      <MoveDown className="h-4 w-4 text-gray-900" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => removeField(field.id)}
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 transition-all duration-200 opacity-0 group-hover:opacity-100 bg-white border border-gray-200"
                      style={{ borderRadius: "8px" }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        Placeholder
                      </Label>
                      <Input
                        value={field.placeholder || ""}
                        onChange={(e) =>
                          updateField(field.id, { placeholder: e.target.value })
                        }
                        placeholder="Texto de ayuda"
                        className="w-full bg-white border border-gray-200 shadow-sm"
                        style={{ borderRadius: "8px" }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        Texto de ayuda
                      </Label>
                      <Input
                        value={field.helpText || ""}
                        onChange={(e) =>
                          updateField(field.id, { helpText: e.target.value })
                        }
                        placeholder="Instrucciones adicionales"
                        className="w-full bg-white border border-gray-200 shadow-sm"
                        style={{ borderRadius: "8px" }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                    <Switch
                      id={`required-${field.id}`}
                      checked={field.required}
                      onCheckedChange={(checked) =>
                        updateField(field.id, { required: checked })
                      }
                      className="data-[state=checked]:bg-orange-500 data-[state=unchecked]:bg-gray-200 border border-gray-300"
                    />
                    <Label
                      htmlFor={`required-${field.id}`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Campo requerido
                    </Label>
                  </div>

                  {/* Lógica condicional */}
                  <div className="space-y-3 border-t pt-4">
                    <div className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                      <Switch
                        id={`conditional-${field.id}`}
                        checked={field.conditionalLogic?.enabled || false}
                        onCheckedChange={(checked) =>
                          updateField(field.id, {
                            conditionalLogic: {
                              ...field.conditionalLogic,
                              enabled: checked,
                            },
                          })
                        }
                        className="data-[state=checked]:bg-orange-500 data-[state=unchecked]:bg-gray-200 border border-gray-300"
                      />
                      <Label
                        htmlFor={`conditional-${field.id}`}
                        className="text-sm font-medium text-gray-700"
                      >
                        Mostrar condicionalmente
                      </Label>
                    </div>

                    {field.conditionalLogic?.enabled && (
                      <div className="grid grid-cols-3 gap-4 mt-2 animate-fadeIn">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700">
                            Depende de
                          </Label>
                          <Select
                            value={field.conditionalLogic.dependsOn || ""}
                            onValueChange={(value) =>
                              updateField(field.id, {
                                conditionalLogic: {
                                  ...field.conditionalLogic,
                                  dependsOn: value,
                                },
                              })
                            }
                          >
                            <SelectTrigger
                              className="w-full bg-white border border-gray-200"
                              style={{
                                borderRadius: "8px",
                              }}
                            >
                              <SelectValue placeholder="Seleccionar campo" />
                            </SelectTrigger>
                            <SelectContent
                              className="bg-white border border-gray-200"
                            >
                              {form.fields
                                .filter((f) => f.id !== field.id)
                                .map((f) => (
                                  <SelectItem key={f.id} value={f.id}>
                                    {f.label}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700">
                            Condición
                          </Label>
                          <Select
                            value={field.conditionalLogic.showWhen || ""}
                            onValueChange={(value) =>
                              updateField(field.id, {
                                conditionalLogic: {
                                  ...field.conditionalLogic,
                                  showWhen: value,
                                },
                              })
                            }
                          >
                            <SelectTrigger
                              className="w-full bg-white border border-gray-200"
                              style={{
                                borderRadius: "8px",
                              }}
                            >
                              <SelectValue placeholder="Seleccionar" />
                            </SelectTrigger>
                            <SelectContent
                              className="bg-white border border-gray-200"
                            >
                              <SelectItem value="equals">Es igual a</SelectItem>
                              <SelectItem value="not_equals">No es igual a</SelectItem>
                              <SelectItem value="contains">Contiene</SelectItem>
                              <SelectItem value="not_contains">No contiene</SelectItem>
                              <SelectItem value="is_empty">Está vacío</SelectItem>
                              <SelectItem value="is_not_empty">No está vacío</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {field.conditionalLogic.showWhen &&
                          field.conditionalLogic.showWhen !== "is_empty" &&
                          field.conditionalLogic.showWhen !== "is_not_empty" && (
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-gray-700">
                                Valor
                              </Label>
                              <Input
                                value={field.conditionalLogic.value || ""}
                                onChange={(e) =>
                                  updateField(field.id, {
                                    conditionalLogic: {
                                      ...field.conditionalLogic,
                                      value: e.target.value,
                                    },
                                  })
                                }
                                placeholder="Valor de comparación"
                                className="w-full bg-white border border-gray-200 shadow-sm"
                                style={{ borderRadius: "8px" }}
                              />
                            </div>
                          )}
                      </div>
                    )}
                  </div>

                  {field.type === "file" && (
                    <div className="space-y-4 mt-4">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id={`multiple-${field.id}`}
                          checked={field.multiple || false}
                          onCheckedChange={(checked) =>
                            updateField(field.id, { multiple: checked })
                          }
                        />
                        <Label
                          htmlFor={`multiple-${field.id}`}
                          style={{ color: "var(--doc-text-primary)" }}
                        >
                          Permitir múltiples archivos
                        </Label>
                      </div>

                      <div className="space-y-2">
                        <Label style={{ color: "var(--doc-text-primary)" }}>
                          Tipos de archivos permitidos
                        </Label>
                        <Input
                          value={
                            field.accept || ".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          }
                          onChange={(e) =>
                            updateField(field.id, { accept: e.target.value })
                          }
                          placeholder=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          style={{ borderRadius: "8px" }}
                        />
                        <p className="text-xs text-muted-foreground">
                          Separar con comas. Ejemplo: .pdf,.doc,.jpg
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label style={{ color: "var(--doc-text-primary)" }}>
                          Tamaño máximo (MB)
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={field.maxSize || 10}
                          onChange={(e) =>
                            updateField(field.id, {
                              maxSize: Number.parseInt(e.target.value),
                            })
                          }
                          placeholder="10"
                          style={{ borderRadius: "8px" }}
                        />
                      </div>
                    </div>
                  )}

                  {(field.type === "select" || field.type === "radio") && (
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <Label style={{ color: "var(--doc-text-primary)" }}>
                          Opciones
                        </Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addOption(field.id)}
                          style={{ borderRadius: "8px" }}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Añadir opción
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {field.options.map((option) => (
                          <div
                            key={option.id}
                            className="flex items-center space-x-2"
                          >
                            <Input
                              value={option.label}
                              onChange={(e) =>
                                updateOption(
                                  field.id,
                                  option.id,
                                  e.target.value
                                )
                              }
                              placeholder="Etiqueta de opción"
                              style={{ borderRadius: "8px" }}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeOption(field.id, option.id)}
                              style={{ borderRadius: "50%" }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        {field.options.length === 0 && (
                          <p className="text-sm text-muted-foreground">
                            No hay opciones. Añade al menos una opción.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {field.type === "date" && (
                    <div className="mt-4 space-y-2">
                      <Label style={{ color: "var(--doc-text-primary)" }}>
                        Configuración de fecha
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label style={{ color: "var(--doc-text-primary)" }}>
                            Fecha mínima
                          </Label>
                          <Input
                            type="date"
                            value={field.minDate || ""}
                            onChange={(e) =>
                              updateField(field.id, { minDate: e.target.value })
                            }
                            style={{ borderRadius: "8px" }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label style={{ color: "var(--doc-text-primary)" }}>
                            Fecha máxima
                          </Label>
                          <Input
                            type="date"
                            value={field.maxDate || ""}
                            onChange={(e) =>
                              updateField(field.id, { maxDate: e.target.value })
                            }
                            style={{ borderRadius: "8px" }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {field.type === "time" && (
                    <div className="mt-4 space-y-2">
                      <Label style={{ color: "var(--doc-text-primary)" }}>
                        Configuración de hora
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label style={{ color: "var(--doc-text-primary)" }}>
                            Hora mínima
                          </Label>
                          <Input
                            type="time"
                            value={field.minTime || ""}
                            onChange={(e) =>
                              updateField(field.id, { minTime: e.target.value })
                            }
                            style={{ borderRadius: "8px" }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label style={{ color: "var(--doc-text-primary)" }}>
                            Hora máxima
                          </Label>
                          <Input
                            type="time"
                            value={field.maxTime || ""}
                            onChange={(e) =>
                              updateField(field.id, { maxTime: e.target.value })
                            }
                            style={{ borderRadius: "8px" }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <Button
            variant="outline"
            className="w-full mt-6 border-dashed border-2 h-16 text-gray-500 hover:text-orange-500 hover:border-orange-500 transition-all duration-200 hover:scale-[1.02]"
            style={{
              borderColor: "#f97316",
              borderRadius: "12px",
            }}
            onClick={addField}
          >
            <Plus className="h-5 w-5 mr-2" />
            Añadir Campo
          </Button>
        </CardContent>
        <CardFooter
          className="flex justify-between bg-gray-50 p-6"
          style={{ borderRadius: "0 0 16px 16px" }}
        >
          <div className="flex space-x-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  className="transition-all duration-200 hover:scale-105"
                  style={{ 
                    borderRadius: "8px",
                    backgroundColor: "#ef4444",
                    color: "white"
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {deletingForm ? "Eliminando..." : "Eliminar Formulario"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción no se puede deshacer. Esto eliminará permanentemente el formulario.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteForm}>
                    {deletingForm ? "Eliminando..." : "Eliminar Formulario"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={saveForm}
              variant="outline"
              className="transition-all duration-200 hover:scale-105 border-gray-200"
              style={{ borderRadius: "8px" }}
              disabled={updatingForm || deletingForm}
            >
              {updatingForm ? (
                <span className="animate-pulse flex items-center">
                  <Save className="h-4 w-4 mr-2" />
                  Guardando...
                </span>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar
                </>
              )}
            </Button>

            <Button
              onClick={handleShareForm}
              variant="default"
              className="transition-all duration-200 hover:scale-105"
              style={{
                borderRadius: "8px",
                backgroundColor: "#f97316",
                color: "white",
              }}
            >
              <Share2 className="h-4 w-4 mr-2" />
              {form.finalized ? "Compartir" : "Finalizar y Compartir"}
            </Button>
          </div>
        </CardFooter>
      </Card>

      {/* Diálogo de finalización */}
      <Dialog open={showFinalizeDialog} onOpenChange={setShowFinalizeDialog}>
        <DialogContent
          className="bg-background"
          style={{
            borderRadius: "16px",
            border: "1px solid var(--doc-border)",
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: "var(--color-secondary)" }}>
              Finalizar formulario
            </DialogTitle>
            <DialogDescription>
              ¿Deseas finalizar este formulario? Una vez finalizado, podrás compartirlo con otras personas para que lo completen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowFinalizeDialog(false)}
              className="transition-all duration-200"
              style={{ borderRadius: "8px" }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleFinalizeForm}
              className="transition-all duration-200"
              style={{
                backgroundColor: "orange",
                color: "white",
                borderRadius: "8px",
              }}
            >
              Finalizar formulario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de compartir */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent
          className="bg-background"
          style={{
            borderRadius: "16px",
            border: "1px solid var(--doc-border)",
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: "var(--color-secondary)" }}>
              Compartir formulario
            </DialogTitle>
            <DialogDescription>
              Comparte este enlace para que otras personas puedan completar tu formulario.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-3 mt-4">
            <Input
              value={shareLink}
              readOnly
              className="bg-muted/30"
              style={{ borderRadius: "8px" }}
            />
            <Button
              variant="outline"
              size="icon"
              onClick={copyShareLink}
              className="transition-all duration-200"
              style={{ borderRadius: "8px" }}
            >
              <Link className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowShareDialog(false)}
              className="transition-all duration-200"
              style={{
                backgroundColor: "var(--color-secondary)",
                color: "white",
                borderRadius: "8px",
              }}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

